import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation, withTranslation } from 'react-i18next';
import compose from 'ramda/src/compose';
import { Box, Container } from '@material-ui/core';
import SubscriptionLogoStatus from '../components/subscriptionLogoStatus';
import SubscriptionContainer from '../components/subscriptionContainer';
import PaymentMethod from '../components/paymentMethod';
import useRetrieveDefaultPayment from '../../common/hooks/useRetrieveDefaultPayment';
import useMetaDataCodes from '../../common/hooks/useMetaDataCodes';
import LocaleKeys from '../../locales/keys';
import useRetrieveSubscription from '../../common/hooks/useRetrieveSubscription';
import ErrorScreen from '../../common/errorScreen';
import { getUpdatedPaymentInstrumentPayload } from '../helpers/paymentMethodFormSubmit';
import useUpdatePaymentInstrument from '../../common/hooks/useUpdatePaymentInstrument';
import useProductData from '../../common/hooks/useProductData';
import { parseIntroPricing } from '../../utils/parseIntroPricing';
import {
  getPlanImageList,
  getSubscriptionDate,
  subscriptionProperties,
} from '../helpers/subscriptionBasedProductCodeTables';
import {
  getIsFreeTrialNode,
  getPaymentInstrumentList,
  getPaymentListModel,
  getPricingPlan,
  getProductIdFromSubscription,
  getSubscriptionListModel,
  isSequenceUpdateFormNode,
  setPaymentMethodNode,
  setRetrievePaymentInstrumentNode,
  getAddNewAddressAddressList,
  setCardToStripeRetrievePaymentInstrumentStripeNode,
} from '../helpers/mySubscriptionHelpers';
import { addNewAddressConstant } from '../../constants/addNewAddressOption.constant';
import useStripePaymentList from '../../common/hooks/useStripePaymentList';
import { useUpdateEffect } from '../../common/hooks/useUpdateEffect';
import { scrollOptions, scrollToTop } from '../../utils/common';
import { postMessage } from '../../utils/postMessage';
import useRemovePaymentInstrument from '../../common/hooks/useRemovePaymentInstrument';
import useCreatePaymentInstrument from '../../common/hooks/useCreatePaymentInstrument';
import requestUpdateMatchingSubscriptions from '../../utils/requestUpdateMatchingSubscriptions';
import useUpdateMultipleSubscriptions from '../../common/hooks/useUpdateMultipleSubscriptions';
import UpgradeSubscription from '../components/upgradeSubscription';
import {
  ELIGIBILITY_ACTION_EXTERNAL_OPIN,
  ELIGIBILITY_ACTION_PURCHASE,
} from '../../constants/upgradeJourney.constant';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: '100%',
    backgroundColor: theme.palette.background.paper,
  },
  container: {
    backgroundColor: theme.palette.custom.white,
    borderRadius: '10px',
    border: `2px solid ${theme.palette.custom.frame}`,
    padding: '30px',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: '20px',
  },
  headerText: {
    textTransform: 'uppercase',
    padding: '10px 0 20px 0',
  },
  boxContainer: {
    marginBottom: '0px',
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    textTransform: 'capitalize',
  },
  logoRowContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    textTransform: 'capitalize',
    marginBottom: '5px',
  },
  nav: {
    width: '100%',
    maxWidth: '100%',
    backgroundColor: theme.palette.background.paper,
    '&.MuiList-padding': {
      paddingTop: 0,
    },
  },
  iconText: {
    fontFamily: 'Formula1-Wide',
  },
  iconNode: {
    justifyContent: 'space-around',
    display: 'flex',
  },
  buttonNode: {
    borderBottom: `solid 2px ${theme.palette.custom.darkRed}`,
    borderRadius: '0',
    padding: '0',
    fontSize: '15px',
    fontWeight: 700,
    textTransform: 'none',
    fontFamily: 'titillium-semibold',
    WebkitFontSmoothing: 'auto',
    '&:hover': {
      backgroundColor: 'unset',
      color: theme.palette.custom.darkRed,
    },
  },
  changeButtonNode: {
    fontFamily: 'titillium-semibold',
    WebkitFontSmoothing: 'auto',
    borderBottom: `solid 2px ${theme.palette.custom.darkRed}`,
    borderRadius: '0',
    padding: '0',
    textTransform: 'capitalize',
    fontSize: '13px',
    lineHeight: 1,
  },
  baseButtonNode: {
    margin: '20px 0px 40px 0px',
    padding: 0,
  },

  listItemNode: {
    flex: '1 1 50%',
    letterSpacing: '.2px',
    fontSize: '17px',
    lineHeight: '1.5',
    [theme.breakpoints.between('xs', 'md')]: {
      margin: 0,
      width: '100%',
    },
  },
  paymentHeaderNode: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: '20px',
  },
  paymentNode: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: '20px',
  },
  innerNode: {
    display: 'flex',
    justifyContent: 'flex-start',
    width: '50%',
    flexWrap: 'wrap',
    [theme.breakpoints.between('xs', 'md')]: {
      flexDirection: 'column',
    },
  },
  outerNode: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '50%',
    flexWrap: 'wrap',
    [theme.breakpoints.between('xs', 'md')]: {
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-end',
    },
  },
  wcNode: {
    fontFamily: 'Formula1-Bold',
    fontWeight: 700,
    alignSelf: 'center',
    [theme.breakpoints.between('xs', 'md')]: {
      alignSelf: 'flex-start',
    },
  },
  logoContainer: {
    [theme.breakpoints.between('xs', 'md')]: {
      marginBottom: '20px',
    },
  },
  logo: {
    height: 'auto',
    width: 'auto',
    maxWidth: '100%',
  },
  listItemContainer: {
    padding: '15px 0 0 0',
    alignItems: 'flex-start',
    [theme.breakpoints.between('xs', 'md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  freeTrialImageContainer: {
    width: '50%',
    [theme.breakpoints.between('xs', 'md')]: {
      width: '100%',
    },
  },
  affiliateImageContainer: {
    width: '150px',
  },
  paymentHeader: {
    fontFamily: 'Formula1-Regular',
    fontSize: '25px',
    textTransform: 'uppercase',
    letterSpacing: '0.2px',
    paddingTop: '20px',
  },
  paymentText: {
    fontFamily: 'titillium-regular',
    fontSize: '17px',
    letterSpacing: '0.2px',
    textTransform: 'none',
    textDecoration: 'none',
    borderBottom: 'none',
  },
  appStoreHeader: {
    fontFamily: 'Formula1-Regular',
    fontSize: '25px',
    margin: '0 20px 0 0',
    padding: '30px 0 20px',
    letterSpacing: '0.2px',
    textTransform: 'uppercase',
  },
  appStoreText: {
    fontFamily: 'titillium-regular',
    fontSize: '17px',
    letterSpacing: '0.2px',
    textTransform: 'none',
  },
}));

function MySubscriptions(props) {
  const {
    subscriptionListContainer,
    subscriptionFault,
    subscriptionOnMount,
    productExternalReference,
    productExternalReferenceType,
    stripePromise,
    cdStripeHeader,
    setIsReady,
    isPaymentReady,
    setIsPaymentReady,
    allSubscriptions,
    refreshAllSubscriptions,
    retrieveEligibilityUiModel,
  } = props;

  const { t } = useTranslation();
  const classes = useStyles();
  const boxRef = useRef(null);

  /** useSelector */

  const showPageLoader = useSelector(
    (state) => state?.app?.ui?.pageLoader || false,
  );

  const language = useSelector((state) => state.ascendon.settings.language);

  /** web api hooks */
  const [
    externalDataCountries,
    externalDataStates,
    retrieveMetaDataFault,
    requestMetaDataCodes,
  ] = useMetaDataCodes();

  const [
    retrieveSubscriptionUiModel,
    retrievesubscriptionFault,
    requestRetrieveSubscription,
  ] = useRetrieveSubscription();

  const [
    retrieveDefaultPaymentUiModel,
    retrieveDefaultPaymentFault,
    requestRetrieveDefaultPayment,
  ] = useRetrieveDefaultPayment();

  const [
    updatePaymentInstrumentUiModel,
    updatePaymentInstrumentFault,
    updatePaymentInstrumentLoading,
    requestUpdatePaymentInstrument,
    updatePaymentInteractionData,
  ] = useUpdatePaymentInstrument();
  const [
    requestProductDataUiModel,
    requestProductDataFault,
    requestProductData,
  ] = useProductData();
  const [
    removePaymentInstrumentResponse,
    removePaymentInstrumentFault,
    requestRemovePaymentInstrument,
  ] = useRemovePaymentInstrument();
  const [
    retrieveStripePaymentListUiModel,
    retrieveStripePaymentListFault,
    requestStripePaymentList,
  ] = useStripePaymentList();

  const [
    createPaymentInstrumentUiModel,
    createPaymentInstrumentFault,
    createPaymentInstrumentLoading,
    requestCreatePaymentInstrument,
  ] = useCreatePaymentInstrument();

  const [
    isStripeRequestPaymentInstrument,
    setIsStripeRequestPaymentInstrument,
  ] = useState(false);
  const [
    updateMultipleStripeUICallbackResponse,
    setUpdateMultipleStripeUICallbackResponse,
  ] = useState(null);
  const [stripePaymentListUiModel, setStripePaymentListUiModel] = useState({});

  const [
    updateMultipleSubscriptionsUiModel,
    updateMultipleSubscriptionsFault,
    requestUpdateMultipleSubscriptions,
  ] = useUpdateMultipleSubscriptions();

  /** use state */
  const [subscriptionList, setSubscriptionList] = useState([]);
  const [paymentMethodList, setPaymentMethodList] = useState([]);
  const [isChangeClicked, setIsChangeClicked] = useState(false);
  const [statusName, setStatusName] = useState('');
  const [subscriptionId, setSubscriptionId] = useState(0);
  const [editSelectedId, setEditSelectedId] = useState(null);
  const [updatePaymentinstrument, setUpdatePaymentinstrument] = useState(null);
  const [addressList, setAddressList] = useState([]);
  const [retrievePaymentInstrument, setRetrievePaymentInstrument] =
    useState(null);
  const [defaultCard, setDefaultCard] = useState(null);
  const [removePaymentInstrumentStatus, setRemovePaymentInstrumentStatus] =
    useState(false);
  const [errors, setErrors] = useState([]);
  const [imageUrl, setImageUrl] = useState('');
  const [isManagedPricingplan, setIsManagedPricingplan] = useState(false);
  const requestFaults = [
    subscriptionFault,
    retrievesubscriptionFault,
    retrieveDefaultPaymentFault,
    updateMultipleSubscriptionsFault,
    retrieveMetaDataFault,
    requestProductDataFault,
  ];
  const [hidewhiteBackground, setHidewhiteBackground] = useState(false);
  const [hidePaymentMethodContainer, setHidePaymentMethodContainer] =
    useState(false);
  const [isMountedState, setIsMountedState] = useState(true);
  const [paymentMethodFormFault, setPaymentMethodFormFault] = useState('');
  const [isSequenceUpdateForm, setIsSequenceUpdateForm] = useState(false);
  const [
    showRemovePaymentInstrumentFault,
    setShowRemovePaymentInstrumentFault,
  ] = useState(null);
  const [formikStateValues, setFromikStateValues] = useState({});
  const [subscriptionCycleDate, setsubscriptionCycleDate] = useState('');
  const [pricingPlanFromSubscription, setPricingPlanFromSubscription] =
    useState(null);
  const [retrieveSubscriptionDetail, setRetrieveSubscriptionDetail] =
    useState(null);
  const [subscriptionPropertiesUiNode, setSubscriptionPropertiesUiNode] =
    useState({});
  const [isFreeTrial, setIsFreeTrial] = useState(false);
  const [planImageList, setPlanImageList] = useState(null);
  const [addNewAddressStatus, setAddNewAddressStatus] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [isFormError, setIsFormError] = useState(false);
  const [createdPaymentInstrument, setCreatedPaymentInstrument] =
    useState(undefined);
  const [
    isCreatePaymentInstrumentFromStripeToCreditCard,
    setIsCreatePaymentInstrumentFromStripeToCreditCard,
  ] = useState(false);
  const [isUpgradeAvailable, setIsUpgradeAvailable] = useState(true);

  /** Helper functions */
  const getStatusName = (name) => {
    if (name !== 'Active') {
      return name;
    }
    return t(LocaleKeys.subscriptionInfo.subscriptions.statusName);
  };

  const productMetaDataCall = (subscriptionUiModelNode) => {
    const getProductId = getProductIdFromSubscription(subscriptionUiModelNode);
    const { Id, isProductId, PricingPlan } = getProductId;
    setPricingPlanFromSubscription(PricingPlan);
    const subscriptionCurrency = PricingPlan?.Currency || 'USD';
    setRetrieveSubscriptionDetail(subscriptionUiModelNode);
    if (isProductId) {
      requestProductData({
        productExternalReference,
        productExternalReferenceType,
        id: Id,
      });
    } else {
      setSubscriptionList([
        ...getSubscriptionListModel(
          subscriptionUiModelNode,
          subscriptionCurrency,
          {},
          {},
          false,
          t,
        ),
      ]);
    }
  };

  const getSubscriptionUiModel = (subscriptionUiModelNode) => {
    setEditSelectedId(null);
    setIsChangeClicked(false);
    setIsSequenceUpdateForm(false);
    setSubscriptionPropertiesUiNode(
      subscriptionProperties(
        subscriptionUiModelNode?.details,
        stripePaymentListUiModel?.StripePayment,
        retrieveEligibilityUiModel?.EntitledPlans,
      ),
    );
    const subscriptionCycleDate = subscriptionUiModelNode?.details?.CycleDate;
    setsubscriptionCycleDate(getSubscriptionDate(subscriptionCycleDate));
    const getSubscriptionId = subscriptionUiModelNode?.details?.Id;
    const statusNameFromUiModel = subscriptionUiModelNode?.details?.StatusName;
    productMetaDataCall(subscriptionUiModelNode);
    setStatusName(getStatusName(statusNameFromUiModel));
    setSubscriptionId(getSubscriptionId);
    const getSubscriptionNode = [
      ...getPaymentListModel(subscriptionUiModelNode, getSubscriptionId),
    ];
    setDefaultCard(getSubscriptionNode);
    setPaymentMethodList(getSubscriptionNode);
  };

  const filteredPaymentInstrumentList = (paymentInstrumentId) => {
    const filteredPaymentMethodList = paymentMethodList.filter(
      (item) => item.PaymentInstrumentId !== paymentInstrumentId,
    );
    setRemovePaymentInstrumentStatus(true);
    setRetrievePaymentInstrument(filteredPaymentMethodList);
    setPaymentMethodList(defaultCard);
    setEditSelectedId(null);
    setIsChangeClicked(false);
  };

  const removePaymentInstrumentFaultHandler = (
    removePaymentInstrumentFault,
  ) => {
    setShowRemovePaymentInstrumentFault(removePaymentInstrumentFault);
  };

  useEffect(() => {
    if (subscriptionOnMount && retrieveDefaultPaymentUiModel) {
      requestMetaDataCodes('countries');
      requestMetaDataCodes('states');
    }
  }, [props.profileType, retrieveDefaultPaymentUiModel]);

  /**Initial Load */
  useEffect(() => {
    if (isMountedState && subscriptionOnMount) {
      postMessage('module.showLoader', {});
      //requestRetrieveAddresses();
      requestStripePaymentList({ cdStripeHeader });
      requestRetrieveDefaultPayment({ EnforceSessionCountry: false });
    }
    return () => {
      setIsMountedState(false);
    };
  }, []);

  const { defaultPaymentConfiguration } = ASCENDON_ENV_CONFIG?.stripe;
  const defaultStripePaymentListUiModel = {
    StripePayment: {
      ConfigurationId: defaultPaymentConfiguration?.id,
      PaymentTypes: defaultPaymentConfiguration?.paymentTypes,
    },
  };

  useEffect(() => {
    if (retrieveStripePaymentListFault) {
      setStripePaymentListUiModel(defaultStripePaymentListUiModel);
    }
  }, [retrieveStripePaymentListFault]);

  useEffect(() => {
    if (retrieveStripePaymentListUiModel && currentSubscription) {
      if (retrieveStripePaymentListUiModel?.StripePayment) {
        const getSubscriptionDetailsNode = {
          details: currentSubscription,
        };
        getSubscriptionUiModel(getSubscriptionDetailsNode);
      } else {
        setStripePaymentListUiModel(defaultStripePaymentListUiModel);
      }
    }
  }, [retrieveStripePaymentListUiModel, currentSubscription]);

  useEffect(() => {
    if (subscriptionListContainer) {
      const getSubscriptionDetailsNode = {
        details: subscriptionListContainer,
      };
      getSubscriptionUiModel(getSubscriptionDetailsNode);
      setCurrentSubscription(subscriptionListContainer);
    }
  }, [subscriptionListContainer]);

  useEffect(() => {
    if (retrieveSubscriptionUiModel) {
      getSubscriptionUiModel(retrieveSubscriptionUiModel);
      setCurrentSubscription(retrieveSubscriptionUiModel?.details);
    }
  }, [retrieveSubscriptionUiModel]);

  const updateSubscription = (updatedSubscription) => {
    requestRetrieveSubscription({
      Id: subscriptionId,
      IncludeChangeOptions: true,
    });
  };

  /** updateSubscriptionUiModel */
  useEffect(() => {
    if (updateMultipleSubscriptionsUiModel) {
      if (isCreatePaymentInstrumentFromStripeToCreditCard) {
        requestRemovePaymentInstrument({ Id: editSelectedId });
        setIsCreatePaymentInstrumentFromStripeToCreditCard(false);
        return;
      }
      requestRetrieveSubscription({
        Id: subscriptionId,
        IncludeChangeOptions: true,
      });
    }
  }, [updateMultipleSubscriptionsUiModel]);

  useEffect(() => {
    if (removePaymentInstrumentResponse) {
      if (
        updateMultipleStripeUICallbackResponse?.length ||
        updateMultipleSubscriptionsUiModel?.length
      ) {
        refreshAllSubscriptions();
      } else {
        requestRetrieveSubscription({
          Id: subscriptionId,
          IncludeChangeOptions: true,
        });
      }
      if (retrievePaymentInstrument?.length > 1) {
        requestRetrieveDefaultPayment({ EnforceSessionCountry: false });
      }
    }
  }, [removePaymentInstrumentResponse]);

  useEffect(() => {
    if (subscriptionFault || requestProductDataFault) {
      setHidewhiteBackground(true);
    } else if (retrievesubscriptionFault) {
      setHidewhiteBackground(true);
    } else if (
      retrieveDefaultPaymentFault ||
      updateMultipleSubscriptionsFault ||
      retrieveMetaDataFault
    ) {
      setHidePaymentMethodContainer(true);
      setHidewhiteBackground(false);
    }
    const filterFaults = requestFaults.filter((fault) => !!fault);
    if (filterFaults.length) {
      const currentFault = filterFaults[0];
      setErrors([...errors, currentFault]);
      setIsReady(true);
    }
  }, [...requestFaults]);

  useEffect(() => {
    if (updatePaymentInstrumentLoading) {
      postMessage('module.showLoader', {});
    } else {
      postMessage('module.hideLoader', {});
    }
  }, [updatePaymentInstrumentLoading]);

  useEffect(() => {
    retrieveDefaultPaymentUiModel &&
      setRetrievePaymentInstrument(retrieveDefaultPaymentUiModel);
  }, [retrieveDefaultPaymentUiModel]);

  const doWeHaveMultipleSubscriptionsWithTheSamePaymentInstrument = (
    paymentInstrumentId,
  ) => {
    const matchingSubscriptions = allSubscriptions?.filter(
      (subscription) =>
        subscription?.PaymentInstrument?.Id === paymentInstrumentId,
    );
    return matchingSubscriptions?.length > 1;
  };

  useEffect(() => {
    if (updatePaymentInstrumentUiModel) {
      if (isStripeRequestPaymentInstrument) {
        const { PaymentInstrument } = updatePaymentInstrumentUiModel;
        const { BillingAddress, StripeAccount } = PaymentInstrument;
        if (
          doWeHaveMultipleSubscriptionsWithTheSamePaymentInstrument(
            updatePaymentInstrumentUiModel?.PaymentInstrument?.Id,
          )
        ) {
          refreshAllSubscriptions();
        } else {
          requestRetrieveSubscription({
            Id: subscriptionId,
            IncludeChangeOptions: true,
          });
        }

        const getRetrievePaymentInstrumentNode =
          setCardToStripeRetrievePaymentInstrumentStripeNode(
            retrievePaymentInstrument,
            editSelectedId,
            StripeAccount,
            BillingAddress,
            PaymentInstrument,
          );
        setRetrievePaymentInstrument(getRetrievePaymentInstrumentNode);
        return;
      }
      if (isSequenceUpdateForm) {
        const { PaymentInstrument } = updatePaymentInstrumentUiModel;
        const { CreditCard } = PaymentInstrument;
        const { Id, PaymentInstrumentId } = updatePaymentinstrument;
        const {
          paymentMethodListValues,
          creditCardDetails,
          billngAddressDetails,
        } = setPaymentMethodNode(
          paymentMethodList,
          PaymentInstrumentId,
          formikStateValues,
          addressList,
          CreditCard,
        );
        const getRetrievePaymentInstrumentNode =
          setRetrievePaymentInstrumentNode(
            retrievePaymentInstrument,
            PaymentInstrumentId,
            creditCardDetails,
            billngAddressDetails,
          );
        const paymentMethodListvalues = paymentMethodListValues.filter(
          (item) => item.PaymentInstrumentId === PaymentInstrumentId,
        );
        boxRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });

        requestUpdateMatchingSubscriptions({
          requestUpdateMultipleSubscriptions,
          allSubscriptions,
          currentSubscriptionId: Id,
          newPaymentInstrumentId: PaymentInstrumentId,
        });

        setPaymentMethodList(paymentMethodListvalues);
        setRetrievePaymentInstrument(getRetrievePaymentInstrumentNode);
        setIsChangeClicked(false);

        if (addNewAddressStatus) {
          const { addressListWithoutAddNewAddressItem, getAddressList } =
            getAddNewAddressAddressList(
              updatePaymentInstrumentUiModel?.PaymentInstrument?.BillingAddress,
              addressList,
            );

          setAddressList([
            ...addressListWithoutAddNewAddressItem,
            ...getAddressList,
            ...addNewAddressConstant(),
          ]);
        } else {
          return;
        }
      }
      setEditSelectedId(null);
    }
  }, [updatePaymentInstrumentUiModel, isStripeRequestPaymentInstrument]);

  useEffect(() => {
    if (updatePaymentInstrumentFault) {
      if (typeof updatePaymentInstrumentFault === 'string') {
        setPaymentMethodFormFault(updatePaymentInstrumentFault);
      }
    }
  }, [updatePaymentInstrumentFault]);

  useEffect(() => {
    if (requestProductDataUiModel?.metadata) {
      const { metadata } = requestProductDataUiModel;
      const { Product } = metadata;
      const { PricingPlans } = Product;

      const getPricingPlanNode = getPricingPlan(
        pricingPlanFromSubscription,
        PricingPlans,
      );
      setImageUrl(Product.ImageUrl);
      setIsManagedPricingplan(
        getPricingPlanNode?.Name?.toUpperCase().includes('VIP') ||
          getPricingPlanNode?.Name?.toUpperCase().includes('OPS'),
      );

      if (getPricingPlanNode && getPricingPlanNode.AdditionalInformationURL) {
        setImageUrl(getPricingPlanNode.AdditionalInformationURL);
      }
      const parseIntroPricingNode = parseIntroPricing(getPricingPlanNode);
      const freeTrial = getIsFreeTrialNode(getPricingPlanNode, 'FreeTrial');
      setIsFreeTrial(freeTrial);
      const classOptions = {
        freeTrial: classes.freeTrialImageContainer,
        affiliate: classes.affiliateImageContainer,
      };
      setPlanImageList(
        getPlanImageList(freeTrial, subscriptionPropertiesUiNode, classOptions),
      );
      const subscriptionCurrency =
        pricingPlanFromSubscription?.Currency || 'USD';
      setSubscriptionList([
        ...getSubscriptionListModel(
          retrieveSubscriptionDetail,
          subscriptionCurrency,
          subscriptionPropertiesUiNode,
          parseIntroPricingNode,
          freeTrial,
          t,
        ),
      ]);
    }
  }, [requestProductDataUiModel]);

  useUpdateEffect(() => {
    if (showPageLoader) {
    } else {
      setTimeout(() => {
        const pageHeight = document.getElementById(
          scrollOptions.id,
        ).offsetHeight;
        scrollToTop(pageHeight, 0, scrollOptions.behavior);
      }, 100);
    }
  }, [showPageLoader]);

  useEffect(() => {
    if (createPaymentInstrumentUiModel) {
      const { PaymentInstrument } = createPaymentInstrumentUiModel;
      const { Id: PaymentInstrumentId } = PaymentInstrument;
      requestUpdateMatchingSubscriptions({
        requestUpdateMultipleSubscriptions,
        allSubscriptions,
        currentSubscriptionId: subscriptionId,
        newPaymentInstrumentId: PaymentInstrumentId,
      });
    }
  }, [createPaymentInstrumentUiModel]);

  useEffect(() => {
    if (createPaymentInstrumentFault) {
      setPaymentMethodFormFault(createPaymentInstrumentFault);
    }
  }, [createPaymentInstrumentFault]);

  useEffect(() => {
    if (createPaymentInstrumentLoading) {
      postMessage('module.showLoader', {});
    } else {
      postMessage('module.hideLoader', {});
    }
  }, [createPaymentInstrumentLoading]);

  useEffect(() => {
    if (isFormError) {
      postMessage('module.hideLoader', {});
    }
  }, [isFormError]);

  useEffect(() => {
    if (retrieveEligibilityUiModel) {
      setIsUpgradeAvailable(true);
    }
  }, [retrieveEligibilityUiModel]);

  /** Event handlers */
  const handleChangeClick = () => {
    const elementSort = (a, b) => b.Id - a.Id;
    const getSortedDefaultPaymentUiModel = (defaultPaymentUiModel) =>
      defaultPaymentUiModel.sort(elementSort);
    const sortedDefaultPaymentUiModel = getSortedDefaultPaymentUiModel(
      retrievePaymentInstrument,
    );
    const getNode = [
      ...getPaymentInstrumentList(
        sortedDefaultPaymentUiModel,
        subscriptionId,
        removePaymentInstrumentStatus,
      ),
    ];
    setPaymentMethodList(getNode);
    setIsChangeClicked(true);
  };

  const handleSelectClick = (selectedNode) => {
    boxRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    const { Id, PaymentInstrumentId } = selectedNode;

    requestUpdateMatchingSubscriptions({
      requestUpdateMultipleSubscriptions,
      allSubscriptions,
      currentSubscriptionId: Id,
      newPaymentInstrumentId: PaymentInstrumentId,
    });
  };

  const handleEditClick = (selectedEditNode) => {
    if (editSelectedId) {
      return;
    }
    const selectedPaymentInstrument =
      retrievePaymentInstrument.find(
        (item) => item?.Id === selectedEditNode?.PaymentInstrumentId,
      ) || currentSubscription?.PaymentInstrument;
    const getSubscriptionDetailsNode = {
      details: {
        ...currentSubscription,
        PaymentInstrument: selectedPaymentInstrument,
      },
    };
    setSubscriptionPropertiesUiNode(
      subscriptionProperties(
        getSubscriptionDetailsNode?.details,
        stripePaymentListUiModel?.StripePayment,
        retrieveEligibilityUiModel?.EntitledPlans,
      ),
    );
    setEditSelectedId(selectedEditNode?.PaymentInstrumentId);
  };

  const handleButtonCancel = (selectedEditNode) => {
    setPaymentMethodFormFault('');
    setEditSelectedId(null);
    setShowRemovePaymentInstrumentFault(null);
  };

  const handleSaveStripePayment = (
    stripePayload,
    isCreditCardToStripe = false,
    updateMultipleStripeUIResponse = null,
  ) => {
    if (isCreditCardToStripe) {
      requestRemovePaymentInstrument({ Id: editSelectedId });
      setUpdateMultipleStripeUICallbackResponse(updateMultipleStripeUIResponse);

      return;
    }
    const { updatePaymentInstrumentPayload } = stripePayload;
    setIsStripeRequestPaymentInstrument(true);
    requestUpdatePaymentInstrument({
      ...updatePaymentInstrumentPayload,
    });
  };

  useEffect(() => {
    removePaymentInstrumentFault &&
      removePaymentInstrumentFaultHandler(removePaymentInstrumentFault);
  }, [removePaymentInstrumentFault]);

  const stripeToCreditCardActivity = (
    getUpdatedPaymentInstruments,
    isStripeToCreditCard,
  ) => {
    const defaultPaymentInstrumentValues = {
      Default: true,
      Type: 0,
      TypeName: null,
    };
    const { StripeAccount, ...getPayloadForCreatePaymentInstrument } =
      getUpdatedPaymentInstruments;
    requestCreatePaymentInstrument({
      PaymentInstrument: {
        ...getPayloadForCreatePaymentInstrument,
        ...defaultPaymentInstrumentValues,
      },
    });
    setIsCreatePaymentInstrumentFromStripeToCreditCard(isStripeToCreditCard);
  };

  const handleSave = (saveNode, isStripeToCreditCard) => {
    const { name, formik, showAddNewAddress, postalCodeRegularExpression } =
      saveNode;
    const { initialValues, values } = formik;
    const { SecurityCode, ...restUpdateValues } = values;
    setFromikStateValues({ ...values });
    setPaymentMethodFormFault('');
    setUpdatePaymentinstrument({
      Id: subscriptionId,
      PaymentInstrumentId: name,
    });
    const getSelectedPaymentInstrument = retrievePaymentInstrument.find(
      (item) => item.Id === name,
    );
    const getUpdatedPaymentInstruments = getUpdatedPaymentInstrumentPayload(
      values,
      getSelectedPaymentInstrument,
      showAddNewAddress,
      postalCodeRegularExpression,
      retrievePaymentInstrument,
    ); // Used for cyber source payment instrument

    const getisSequenceFormUpdate = isSequenceUpdateFormNode(
      initialValues,
      restUpdateValues,
    );
    setIsSequenceUpdateForm(getisSequenceFormUpdate);
    if (isStripeToCreditCard) {
      stripeToCreditCardActivity(
        getUpdatedPaymentInstruments,
        isStripeToCreditCard,
      );
    } else {
      requestUpdatePaymentInstrument({
        PaymentInstrument: { ...getUpdatedPaymentInstruments },
      });
    }
    setAddNewAddressStatus(showAddNewAddress);
  };

  const renderSubscriptionDetails = () => (
    <Container
      mb={10}
      className={`${!hidewhiteBackground && classes.container}`}
      width={733}
    >
      {errors && errors.length && !hidePaymentMethodContainer ? (
        <Box mb={6}>
          <ErrorScreen
            error={errors[0]}
            mt={5}
            mb={5}
            variant="body1"
            hideBackdrop={true}
          />
        </Box>
      ) : (
        <Box mt={1} ml={1} mr={1}>
          <Box>
            <Box
              mb={1}
              mt={1}
              className={classes.root}
              ref={(element) => (boxRef.current = element)}
            >
              <Box display="flex" flexDirection="column" gridRowGap={16}>
                <SubscriptionLogoStatus
                  classes={classes}
                  imageUrl={imageUrl}
                  statusName={statusName}
                  planImageList={planImageList}
                />
                <SubscriptionContainer
                  classes={classes}
                  subscriptionList={subscriptionList}
                  cancelSubscriptionLabel={t(
                    LocaleKeys.subscriptionInfo.subscriptions
                      .subscriptionItemCancelSubscriptionButtonLabel,
                  )}
                  subscriptionProps={subscriptionPropertiesUiNode}
                  subscriptionCycleDate={subscriptionCycleDate}
                  isFreeTrial={isFreeTrial}
                  isAccountNumber={
                    currentSubscription?.PaymentInstrument?.CreditCard
                      ?.AccountNumber ||
                    currentSubscription?.PaymentInstrument?.StripeAccount
                      ?.PaymentMethodId
                  }
                  subId={subscriptionId}
                  isManagedPricingplan={isManagedPricingplan}
                  setErrors={setErrors}
                  onUpdate={updateSubscription}
                  imageUrl={imageUrl}
                  statusName={statusName}
                  planImageList={planImageList}
                  retrieveEligibilityUiModel={retrieveEligibilityUiModel}
                  allSubscriptions={allSubscriptions}
                />
                {isUpgradeAvailable &&
                  currentSubscription &&
                  subscriptionPropertiesUiNode?.eligibilityAction !==
                    ELIGIBILITY_ACTION_PURCHASE &&
                  subscriptionPropertiesUiNode?.eligibilityAction !==
                    ELIGIBILITY_ACTION_EXTERNAL_OPIN && (
                    <UpgradeSubscription
                      eligibilityList={retrieveEligibilityUiModel}
                      currentSubscription={currentSubscription}
                      subscriptionProps={subscriptionPropertiesUiNode}
                      language={language}
                    />
                  )}
                {!hidePaymentMethodContainer &&
                !subscriptionPropertiesUiNode.isIap &&
                !subscriptionPropertiesUiNode.isAffiliateplan ? (
                  <Box>
                    <PaymentMethod
                      classes={classes}
                      paymentMethodList={paymentMethodList}
                      handleChange={handleChangeClick}
                      handleSelectClick={handleSelectClick}
                      handleEditClick={handleEditClick}
                      isChangeClicked={isChangeClicked}
                      paymentInstrumentLength={
                        retrievePaymentInstrument?.length
                      }
                      editSelectedId={editSelectedId}
                      handleButtonCancel={handleButtonCancel}
                      handleSave={handleSave}
                      paymentMethodFormFault={paymentMethodFormFault}
                      filteredPaymentInstrumentList={
                        filteredPaymentInstrumentList
                      }
                      removePaymentInstrumentFaultHandler={
                        removePaymentInstrumentFaultHandler
                      }
                      showRemovePaymentInstrumentFault={
                        showRemovePaymentInstrumentFault
                      }
                      language={language}
                      externalData={externalDataCountries}
                      externalDataState={externalDataStates}
                      isFreeTrial={isFreeTrial}
                      subscriptionProps={subscriptionPropertiesUiNode}
                      isAccountNumber={
                        currentSubscription?.PaymentInstrument?.CreditCard
                          ?.AccountNumber ||
                        currentSubscription?.PaymentInstrument?.StripeAccount
                          ?.PaymentMethodId
                      }
                      stripePromise={stripePromise}
                      editCardDetailsLabel={t(
                        LocaleKeys.subscriptionInfo.subscriptions
                          .changeCardPayment,
                      )}
                      handleSaveStripePayment={handleSaveStripePayment}
                      setIsFormError={setIsFormError}
                      stripePaymentList={stripePaymentListUiModel}
                      isCheckout={false}
                      updatePaymentInstrumentFault={
                        updatePaymentInstrumentFault
                      }
                      updatePaymentInstrumentUiModel={
                        updatePaymentInstrumentUiModel
                      }
                      defaultCard={defaultCard}
                      subId={subscriptionId}
                      createdPaymentInstrument={createdPaymentInstrument}
                      setCreatedPaymentInstrument={setCreatedPaymentInstrument}
                      isPaymentReady={isPaymentReady}
                      setIsPaymentReady={setIsPaymentReady}
                      updatePaymentInteractionData={
                        updatePaymentInteractionData
                      }
                      allSubscriptions={allSubscriptions}
                    />
                  </Box>
                ) : (
                  <>
                    {errors && errors.length > 0 ? (
                      <ErrorScreen
                        error={errors[0]}
                        mt={5}
                        mb={5}
                        variant="body1"
                        hideBackdrop={true}
                      />
                    ) : null}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Container>
  );

  return (
    <Box>
      {!subscriptionPropertiesUiNode.isAffiliateplan
        ? renderSubscriptionDetails()
        : null}
      {subscriptionPropertiesUiNode?.eligibilityAction ===
        ELIGIBILITY_ACTION_PURCHASE &&
        isUpgradeAvailable &&
        currentSubscription && (
          <Container
            mb={10}
            className={`${!hidewhiteBackground && classes.container}`}
            width={733}
          >
            <Box mt={1} ml={1} mr={1}>
              <Box>
                <Box
                  mb={1}
                  mt={1}
                  className={classes.root}
                  ref={(element) => (boxRef.current = element)}
                >
                  <Box display="flex" flexDirection="column" gridRowGap={16}>
                    {
                      <UpgradeSubscription
                        eligibilityList={retrieveEligibilityUiModel}
                        currentSubscription={currentSubscription}
                        subscriptionProps={subscriptionPropertiesUiNode}
                        language={language}
                      />
                    }
                  </Box>
                </Box>
              </Box>
            </Box>
          </Container>
        )}
      {subscriptionPropertiesUiNode.isAffiliateplan
        ? renderSubscriptionDetails()
        : null}
    </Box>
  );
}

export default compose(withTranslation())(MySubscriptions);
